const PERSIST_KEY = 'checkin_dashboard_prefs_v1'

// Dedupe do incremento otimista em tempo real. O card "CHECK-INS REALIZADOS"
// conta participantes DISTINTOS (total_attended), então re-leituras do mesmo
// participante não podem contar 2x entre um poll e outro. Estes conjuntos são
// zerados a cada SET_OVERVIEW — o servidor é sempre a fonte da verdade.
const rtAttendedIds = new Set()
const rtInsideIds = new Set()

const persistPrefs = (filters, bucket) => {
  try {
    localStorage.setItem(PERSIST_KEY, JSON.stringify({
      period: filters.period,
      tagIds: filters.tagIds,
      operatorId: filters.operatorId,
      bucket
    }))
  } catch (e) { /* localStorage cheio/desligado: ignora */ }
}

export default {
  SET_FILTERS (state, partial) {
    state.filters = { ...state.filters, ...partial }
    persistPrefs(state.filters, state.bucket)
  },

  SET_BUCKET (state, bucket) {
    state.bucket = bucket
    persistPrefs(state.filters, state.bucket)
  },

  HYDRATE_FROM_LOCALSTORAGE (state) {
    try {
      const raw = localStorage.getItem(PERSIST_KEY)
      if (!raw) return
      const saved = JSON.parse(raw)
      if (saved.period) state.filters.period = saved.period
      if (Array.isArray(saved.tagIds)) state.filters.tagIds = saved.tagIds
      if (saved.operatorId) state.filters.operatorId = saved.operatorId
      if (saved.bucket) state.bucket = saved.bucket
    } catch (e) { /* JSON corrompido: ignora */ }
  },

  SET_OVERVIEW (state, payload) {
    state.overview = payload
    // Poll reconciliou: o servidor recontou distintos, então zera o dedupe
    // do incremento otimista pra recomeçar a partir do valor autoritativo.
    rtAttendedIds.clear()
    rtInsideIds.clear()
  },

  /**
   * Incremento otimista em tempo real, disparado por cada evento Pusher
   * (CheckinScan). Faz o card "CHECK-INS REALIZADOS" subir na hora, sem esperar
   * o poll de 60s. O poll continua sendo o reconciliador (recontagem exata de
   * distintos, inside_now no modo feira, velocidade etc.).
   *
   * Regras:
   *  - Só aplica quando o evento casa com os filtros atuais (período que engloba
   *    "agora", tag e operador). Assim, ver um dia passado não muda com leitura
   *    de agora.
   *  - total_attended conta DISTINTOS → dedupe por participant_id.
   *  - inside_now: +1 na entrada / −1 na saída (modo padrão). No modo feira
   *    (fair_dwell) o inside_now é uma estimativa por janela de tempo — deixa
   *    pro poll, não mexe aqui.
   */
  APPLY_REALTIME_SCAN (state, payload) {
    const ov = state.overview
    if (!ov || !ov.attendance || !payload || !payload.event_type) return

    // --- casa com os filtros atuais? ---
    const f = state.filters
    const now = Date.now()
    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0)
    if (f.from) {
      const fromT = f.from instanceof Date ? f.from.getTime() : new Date(f.from).getTime()
      if (Number.isFinite(fromT) && fromT > now) return // janela no futuro
    }
    if (f.to) {
      const toT = f.to instanceof Date ? f.to.getTime() : new Date(f.to).getTime()
      if (Number.isFinite(toT) && toT < startOfToday.getTime()) return // janela num dia passado
    }
    // Consistente com o backend: casa se o PARTICIPANTE possui alguma das tags do
    // filtro (participant_tag_ids), não a tag do evento. Fallback pro tag_id do
    // evento quando o payload não trouxer as tags do participante (retrocompat).
    if (Array.isArray(f.tagIds) && f.tagIds.length) {
      const wanted = f.tagIds.map(Number)
      const ptags = Array.isArray(payload.participant_tag_ids)
        ? payload.participant_tag_ids.map(Number)
        : (payload.tag_id != null ? [Number(payload.tag_id)] : [])
      if (!wanted.some(t => ptags.includes(t))) return
    }
    if (f.operatorId && Number(f.operatorId) !== Number(payload.operator_id)) return

    const att = ov.attendance
    const isFair = ov.inside_now_mode === 'fair_dwell'
    const pid = Number(payload.participant_id)

    if (payload.event_type === 'CheckedIn') {
      let attended = att.total_attended || 0
      if (!rtAttendedIds.has(pid)) {
        rtAttendedIds.add(pid)
        attended += 1
      }
      let inside = ov.inside_now || 0
      if (!isFair && !rtInsideIds.has(pid)) {
        rtInsideIds.add(pid)
        inside += 1
      }
      const rate = att.total_confirmed > 0
        ? Math.round((attended / att.total_confirmed) * 1000) / 10
        : att.rate_pct
      state.overview = {
        ...ov,
        inside_now: inside,
        last_read_at: payload.event_date || ov.last_read_at,
        attendance: { ...att, total_attended: attended, rate_pct: rate }
      }
    } else if (payload.event_type === 'CheckedOut' && !isFair) {
      rtInsideIds.delete(pid)
      state.overview = { ...ov, inside_now: Math.max(0, (ov.inside_now || 0) - 1) }
    }
  },
  SET_TIMELINE (state, payload) { state.timeline = payload || [] },
  SET_INSIGHTS (state, payload) { state.insights = payload || [] },
  SET_BY_TAG (state, payload) { state.byTag = payload || [] },
  SET_BYTAG_WINDOW (state, minutes) { state.byTagWindowMinutes = minutes },
  SET_HEATMAP (state, payload) {
    state.heatmap = payload || { tags: [], matrix: {} }
  },
  SET_OPERATORS (state, payload) { state.operators = payload || [] },
  SET_ANOMALIES (state, payload) { state.anomalies = payload },
  SET_FUNNEL (state, payload) { state.funnel = payload },
  SET_FLOW (state, payload) {
    state.flow = payload || { nodes: [], links: [], total_transitions: 0, unique_tags: 0 }
  },
  SET_TOP_LOCATIONS (state, payload) { state.topLocations = payload || [] },

  // Live feed (Pusher) — prepend novo evento, capa em 30 itens.
  PREPEND_LIVE_EVENT (state, payload) {
    const next = [payload, ...state.liveFeed]
    state.liveFeed = next.slice(0, 30)
  },
  CLEAR_LIVE_FEED (state) { state.liveFeed = [] },
  SET_LIVE_CONNECTED (state, value) { state.liveFeedConnected = !!value },
  SET_LIVE_PAUSED (state, value) { state.liveFeedPaused = !!value },

  SET_TAG_DETAIL (state, payload) { state.tagDetail = payload },
  SET_TAG_DETAIL_LOADING (state, value) { state.tagDetailLoading = !!value },
  SET_TAG_DETAIL_BUCKET (state, bucket) { state.tagDetailBucket = bucket },
  CLOSE_TAG_DETAIL (state) {
    state.tagDetail = null
    state.tagDetailLoading = false
    state.tagDetailField = null
    state.tagDetailFieldSecondary = null
    state.tagDetailFieldData = null
    state.tagDetailFieldLoading = false
  },
  SET_TAG_DETAIL_FIELD (state, fieldRef) { state.tagDetailField = fieldRef },
  SET_TAG_DETAIL_FIELD_SECONDARY (state, fieldRef) { state.tagDetailFieldSecondary = fieldRef },
  SET_TAG_DETAIL_FIELD_DATA (state, payload) { state.tagDetailFieldData = payload },
  SET_TAG_DETAIL_FIELD_LOADING (state, value) { state.tagDetailFieldLoading = !!value },

  SET_OPERATOR_DETAIL (state, payload) { state.operatorDetail = payload },
  SET_OPERATOR_DETAIL_LOADING (state, value) { state.operatorDetailLoading = !!value },
  CLOSE_OPERATOR_DETAIL (state) {
    state.operatorDetail = null
    state.operatorDetailLoading = false
    state.operatorDetailField = null
    state.operatorDetailFieldSecondary = null
    state.operatorDetailFieldData = null
    state.operatorDetailFieldLoading = false
  },
  SET_OPERATOR_DETAIL_FIELD (state, fieldRef) { state.operatorDetailField = fieldRef },
  SET_OPERATOR_DETAIL_FIELD_SECONDARY (state, fieldRef) { state.operatorDetailFieldSecondary = fieldRef },
  SET_OPERATOR_DETAIL_FIELD_DATA (state, payload) { state.operatorDetailFieldData = payload },
  SET_OPERATOR_DETAIL_FIELD_LOADING (state, value) { state.operatorDetailFieldLoading = !!value },

  SET_FIELD_PRIMARY (state, fieldRef) {
    state.fieldAnalysis = { ...state.fieldAnalysis, primary: fieldRef }
  },
  SET_FIELD_SECONDARY (state, fieldRef) {
    state.fieldAnalysis = { ...state.fieldAnalysis, secondary: fieldRef }
  },
  SET_FIELD_DATA (state, payload) {
    state.fieldAnalysis = { ...state.fieldAnalysis, data: payload }
  },
  SET_FIELD_LOADING (state, value) {
    state.fieldAnalysis = { ...state.fieldAnalysis, isLoading: value }
  },
  SET_FILTER_OPTIONS (state, payload) {
    state.filterOptions = {
      tags: payload.tags || [],
      operators: payload.operators || [],
      fields: payload.fields || [],
      active_dates: payload.active_dates || [],
      event: payload.event || null
    }
  },

  SET_POLLING_HANDLE (state, handle) { state.pollingHandle = handle },
  SET_LOADING (state, value) { state.isLoading = value },
  SET_FIRST_LOAD_DONE (state) { state.isFirstLoad = false },
  SET_LAST_UPDATE (state, date) { state.lastUpdate = date },
  SET_ERROR (state, err) { state.error = err }
}
